import type {
  ProjectMapCohort,
  ProjectMapMarker,
  ProjectMapResponse,
  RiskLevel,
} from '@/services/realtimeClient';
import { COHORT_PRESENTATION } from '../projectMapCohorts';

export type MapPoint = { latitude: number; longitude: number; label?: string };
export type Job = Omit<ProjectMapMarker, 'location'> & {
  cohort: ProjectMapCohort;
  source: 'project' | 'lead' | 'estimate';
  sourceId: string;
  location: {
    city: string | null;
    state: string | null;
    latitude: number | null;
    longitude: number | null;
  };
};
export const STAGES: ProjectMapCohort[] = [
  'active',
  'pursuit',
  'estimating',
  'submitted_bid',
  'awarded_pending',
  'completed',
  'lost_bid',
  'no_bid',
  'on_hold',
  'cancelled',
  'planning',
];
export const RISK_COLORS: Record<RiskLevel, string> = {
  LOW: '#78aaa4',
  MODERATE: '#b8a06d',
  HIGH: '#be9273',
  CRITICAL: '#ad7681',
};
export type LayerId =
  | 'jobs'
  | 'value'
  | 'risk'
  | 'clients'
  | 'resources'
  | 'demographics'
  | 'businesses'
  | 'development'
  | 'flood'
  | 'parcels'
  | 'access'
  | 'amenities'
  | 'suppliers';
export type BaseMap = 'dark' | 'street' | 'satellite' | 'terrain';
export interface AtlasFilters {
  search: string;
  stages: ProjectMapCohort[];
  sector: string;
  region: string;
  client: string;
  risk: string;
  year: string;
  location: 'all' | 'located' | 'unlocated';
  nearby: boolean;
}
export const EMPTY_FILTERS: AtlasFilters = {
  search: '',
  stages: [],
  sector: '',
  region: '',
  client: '',
  risk: '',
  year: '',
  location: 'all',
  nearby: false,
};
export const DEFAULT_LAYERS: LayerId[] = ['jobs'];
export const LAYER_GROUPS: Array<{
  title: string;
  items: Array<{ id: LayerId; label: string; detail: string }>;
}> = [
  {
    title: 'Your business',
    items: [
      {
        id: 'jobs',
        label: 'Jobs by lifecycle',
        detail: 'All authorized project and bidding records',
      },
      {
        id: 'value',
        label: 'Contract value',
        detail: 'Bubble area reflects recorded contract value; requires finance access',
      },
      {
        id: 'risk',
        label: 'Project risk',
        detail: 'Recorded risk categories; unknown risk stays unclassified',
      },
      {
        id: 'clients',
        label: 'Client concentration',
        detail: 'Shared canonical client identities; no name-based merging',
      },
      {
        id: 'resources',
        label: 'Crew & equipment',
        detail: 'Current recorded assignments at project sites, not GPS locations',
      },
    ],
  },
  {
    title: 'Market & people',
    items: [
      {
        id: 'demographics',
        label: 'Population & income',
        detail: 'ACS 2024 tract estimates and matching tract boundary',
      },
      {
        id: 'businesses',
        label: 'Business activity',
        detail: 'County employer establishments and employment by industry',
      },
      {
        id: 'development',
        label: 'Development activity',
        detail: 'Mapped construction sites; not a complete permit register',
      },
    ],
  },
  {
    title: 'Site & logistics',
    items: [
      {
        id: 'flood',
        label: 'Flood zones',
        detail: 'FEMA mapped flood polygons; missing coverage is not low risk',
      },
      {
        id: 'parcels',
        label: 'Parcels & zoning',
        detail: 'Authoritative parcel service when configured for this jurisdiction',
      },
      {
        id: 'access',
        label: 'Road access',
        detail: 'Mapped highways and ramps; no live traffic or drive-time estimate',
      },
      {
        id: 'suppliers',
        label: 'Suppliers',
        detail: 'Mapped hardware, building material and trade-supply locations',
      },
      {
        id: 'amenities',
        label: 'Nearby amenities',
        detail: 'Mapped groceries, food, health, schools, transit and parks',
      },
    ],
  },
];
export function jobsFromSnapshot(data: ProjectMapResponse): Job[] {
  return [
    ...data.markers.map(
      (job) =>
        ({
          ...job,
          cohort:
            job.cohort ??
            (job.status === 'COMPLETED'
              ? 'completed'
              : job.status === 'ACTIVE'
                ? 'active'
                : 'planning'),
          source: job.source ?? 'project',
          sourceId: job.sourceId ?? job.id,
        }) as Job,
    ),
    ...(data.unlocated ?? []).map(
      (job): Job => ({
        ...job,
        status: job.status ?? '',
        phase: job.phase ?? '',
        clientName: job.clientName ?? null,
        sector: job.sector ?? null,
        riskLevel: job.riskLevel ?? null,
        healthScore: null,
        financials: job.financials ?? null,
        activeAlerts: job.activeAlerts ?? null,
        schedule: job.schedule ?? {
          startDate: null,
          substantialCompletion: null,
          daysToCompletion: null,
        },
        location: { city: job.city, state: job.state, latitude: null, longitude: null },
      }),
    ),
  ];
}
export function jobPoint(job: Job): MapPoint | null {
  const { latitude, longitude } = job.location;
  return latitude !== null &&
    longitude !== null &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    Math.abs(latitude) <= 90 &&
    Math.abs(longitude) <= 180
    ? { latitude, longitude, label: job.name }
    : null;
}
export function distanceMiles(a: MapPoint, b: MapPoint): number {
  const rad = Math.PI / 180;
  const dLat = (b.latitude - a.latitude) * rad,
    dLon = (b.longitude - a.longitude) * rad;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.latitude * rad) * Math.cos(b.latitude * rad) * Math.sin(dLon / 2) ** 2;
  return 3958.7613 * 2 * Math.asin(Math.sqrt(Math.min(1, Math.max(0, h))));
}
export function filterJobs(
  jobs: Job[],
  filter: AtlasFilters,
  center: MapPoint | null,
  radius: number,
): Job[] {
  const search = filter.search.trim().toLocaleLowerCase();
  return jobs.filter((job) => {
    const point = jobPoint(job);
    return (
      (!search ||
        [job.name, job.projectNumber, job.clientName, job.location.city, job.location.state]
          .filter(Boolean)
          .join(' ')
          .toLocaleLowerCase()
          .includes(search)) &&
      (!filter.stages.length || filter.stages.includes(job.cohort)) &&
      (!filter.sector || job.sector === filter.sector) &&
      (!filter.region || job.location.state === filter.region) &&
      (!filter.client || job.canonicalClientIdentityId === filter.client) &&
      (!filter.risk ||
        (filter.risk === 'unknown' ? !job.riskLevel : job.riskLevel === filter.risk)) &&
      (!filter.year ||
        (filter.year === 'unknown'
          ? !job.schedule.startDate
          : job.schedule.startDate?.slice(0, 4) === filter.year)) &&
      (filter.location === 'all' || (filter.location === 'located' ? !!point : !point)) &&
      (!filter.nearby || (!!center && !!point && distanceMiles(center, point) <= radius))
    );
  });
}
export function jobHref(job: Job): string {
  return job.source === 'lead'
    ? '/estimating/leads/' + encodeURIComponent(job.sourceId)
    : job.source === 'estimate'
      ? '/estimating/estimates/' + encodeURIComponent(job.sourceId)
      : '/projects/' + encodeURIComponent(job.sourceId);
}
export function money(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value)))
    return 'Unavailable';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(Number(value));
}
export function number(value: number | null | undefined): string {
  return value === null || value === undefined
    ? 'Unavailable'
    : new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value);
}
export function stageLabel(stage: ProjectMapCohort) {
  return COHORT_PRESENTATION[stage].label;
}
export function csvCell(value: unknown): string {
  let text = value === null || value === undefined ? '' : String(value);
  // Neutralize spreadsheet formulas, including whitespace-prefixed payloads.
  if (/^[\s]*[=+\-@]/.test(text)) text = "'" + text;
  return '"' + text.replaceAll('"', '""') + '"';
}
export function jobsCsv(jobs: Job[]): string {
  const rows = [
    [
      'Project',
      'Number',
      'Stage',
      'Source status',
      'Client',
      'Sector',
      'City',
      'State',
      'Latitude',
      'Longitude',
      'Contract value USD',
      'Risk',
      'Start date',
      'Source',
      'Source ID',
    ],
    ...jobs.map((j) => [
      j.name,
      j.projectNumber,
      stageLabel(j.cohort),
      j.status,
      j.clientName,
      j.sector,
      j.location.city,
      j.location.state,
      j.location.latitude,
      j.location.longitude,
      j.financials?.contractValue,
      j.riskLevel,
      j.schedule.startDate,
      j.source,
      j.sourceId,
    ]),
  ];
  return '\uFEFF' + rows.map((row) => row.map(csvCell).join(',')).join('\r\n');
}
